// Libs
import { FieldType, ViewModelBase, ApiResult } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import validator from "validator";

// App
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import { SaveStore } from "Globals/Stores/Domain";

//Models
import { PreviousAddressModel } from "Globals/Models/Domain/FormSections/PersonalDetails/PreviousAddressModel";
import { PreviousAddressDTO } from "Globals/Models/DTO/PreviousAddressDTO";

// View Models
import PersonalDetailsViewModel from "./PersonalDetailsViewModel";

export default class PereviousAddressViewModel extends ViewModelBase<PreviousAddressModel> {
    private saveStore: SaveStore = StoresInstance.Domain.SaveStore;

    constructor(previousAddressModel?: PreviousAddressModel) {
        super(previousAddressModel ? previousAddressModel : new PreviousAddressModel());
    }

    @action
    public stash = async (personalDetails: PersonalDetailsViewModel): Promise<void> => {
        if ((await this.isModelValid()) && this.isModelDirty) {
            let addressModel = new PreviousAddressModel();
            addressModel.fromDto(this.model);
            let addressViewModel = new PereviousAddressViewModel(addressModel);

            runInAction(() => {
                personalDetails.previousAddressViewModels.push(addressViewModel);
                // triggers the auto-save.
                personalDetails.setDirty("id", true);
            });

            this.model.resetModel();
        }
    };

    @action
    public saveFromStash = async (personalDetailId: string): Promise<void> => {
        try {
            this.setIsLoading(true);
            this.saveStore.setIsSaving(true);

            this.model.personalDetailId = personalDetailId;

            let upsertFamilyAddress: PreviousAddressDTO = this.model.toDto();
            const apiResult = await this.Post<PreviousAddressDTO>(AppUrls.Server.ApplicationForm.Upsert.PreviousAddress, upsertFamilyAddress);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                console.info("PreviousAddress:Upsert-Successful");
                this.setModelDirty(false);
                return Promise.resolve();
            } else {
                console.error("PreviousAddress:Upsert-Error", apiResult.errors);
                return Promise.reject(apiResult.errors);
            }
        } catch (exception) {
            // Set server validation error messages.
            console.log("Could not save from Stash.");
            console.log(exception);
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public delete = async (personalDetails: PersonalDetailsViewModel): Promise<void> => {
        try {
            if (!this.model.getValue("personalDetailId")) {
                runInAction(() => {
                    let index = personalDetails.previousAddressViewModels.findIndex((vm) => vm.getValue("id") == this.model.id);
                    if (index > -1) {
                        personalDetails.previousAddressViewModels.splice(index, 1);
                    }
                });
            } else {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                this.model.personalDetailId = personalDetails.model.id ? personalDetails.model.id : "";
                this.model.isDeleted = true;

                let upsertPreviousAddress: PreviousAddressDTO = this.model.toDto();
                const apiResult = await this.Post<PreviousAddressDTO>(AppUrls.Server.ApplicationForm.Upsert.PreviousAddress, upsertPreviousAddress);

                if (apiResult.wasSuccessful) {
                    this.model.fromDto(apiResult.payload);
                    console.info("PreviousAddress:Upsert-Successful");

                    // Add / remove address into array
                    let addressModel = new PreviousAddressModel();
                    addressModel.fromDto(apiResult.payload);
                    let addressViewModel = new PereviousAddressViewModel(addressModel);
                    const filtered = personalDetails.previousAddressViewModels.filter((a) => a.model.isDeleted == false);
                    runInAction(() => {
                        personalDetails.previousAddressViewModels = filtered;
                    });

                    this.model.resetModel(); // Clear form
                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("PreviousAddress:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            // Set server validation error messages.
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public setDate(fieldName: keyof FieldType<PreviousAddressModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.setValue(fieldName, val);
        }
    }

    @computed
    public get getFormattedFromDate(): string {
        return moment.utc(this.model.fromDate).local().format("DD/MM/YYYY");
    }
    @computed
    public get getFormattedToDate(): string {
        return moment.utc(this.model.toDate).local().format("DD/MM/YYYY");
    }

    @computed
    public get canProceed(): boolean {
        let completed = true;
        return completed;
    }

    @computed
    public get isModelDirty(): boolean {
        let dirty = false;
        Object.keys(this.model).forEach((field) => {
            if (this.model.getDirty(field as keyof FieldType<PreviousAddressModel>)) {
                dirty = true;
            }
        });
        return dirty;
    }
    @action
    public setModelDirty(value: boolean): void {
        Object.keys(this.model).forEach((field) => {
            this.model.setDirty(field as keyof FieldType<PreviousAddressModel>, value);
        });
    }

    public async isFieldValid(fieldName: keyof FieldType<PreviousAddressModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
